/**
 * CMS Utility Functions
 *
 * Provides CMS-specific data transformations including:
 * - Optimized image retrieval with CDN support
 * - Content photo type selection with device preference fallback
 * - Tags categorization guide
 *
 * Ported from App-WebTV/src/utils/cms.utils.js
 * Adapted for FrontendClient (Next.js) — default device is 'web' instead of 'TV'.
 */

import { getOptimizedCDNUrl } from "./cdn.utils";

/**
 * Get the full CDN URL for an image with optimization params
 * Uses ?auto=1&hint=wifi by default for network-aware optimization
 *
 * @param {string} imageName - The image filename
 * @param {Object} [options] - CDN options (auto, hint, width, quality, format)
 * @returns {string|null} Full CDN URL or null
 */
export const getCMSImageURL = (imageName, options = {}) => {
    if (!imageName) return null;
    return getOptimizedCDNUrl(imageName, options);
};

/**
 * Get optimized image data
 * Returns an object with src (progressive JPEG from CDN)
 *
 * @param {object} photoItem - Photo object from contentPhotos array
 * @param {Object} [options] - CDN options (quality, width, etc.)
 * @returns {object} { src }
 */
export const getOptimizedImageData = (photoItem, options = {}) => {
    if (!photoItem) return { src: null };

    const srcPath = photoItem.src;

    return {
        src: srcPath ? getCMSImageURL(srcPath, options) : null,
    };
};

/**
 * Find and get optimized image data for a specific type from contentPhotos
 * @param {string} type - Image type (e.g., 'verticalCover', 'sliderCover', 'poster')
 * @param {Array} contentPhotos - Array of photo objects
 * @param {string} preferredDevice - Preferred device type ('web', 'TV', 'mobile', etc.)
 * @param {Object} [options] - CDN options (quality, width, etc.)
 * @returns {object} { src }
 */
export const getOptimizedImageForType = (
    type = "",
    contentPhotos = [],
    preferredDevice = "web",
    options = {}
) => {
    if (!contentPhotos || !Array.isArray(contentPhotos)) {
        return { src: null };
    }

    // Try to find image with preferred device first
    let photoItem = contentPhotos.find(
        (x) => x.type === type && x.device === preferredDevice
    );

    // Fallback: try TV device (CMS often has TV-optimized assets)
    if (!photoItem && preferredDevice !== "TV") {
        photoItem = contentPhotos.find(
            (x) => x.type === type && x.device === "TV"
        );
    }

    // Fallback to any device for this type
    if (!photoItem) {
        photoItem = contentPhotos.find((x) => x.type === type);
    }

    // Fallback to poster type
    if (!photoItem) {
        photoItem = contentPhotos.find((x) => x.type === "poster");
    }

    return getOptimizedImageData(photoItem, options);
};

/**
 * Legacy function - get image URL for a specific type
 * @deprecated Use getOptimizedImageForType instead
 */
export const getImageForType = (type = "", contentPhotos = []) =>
    getCMSImageURL(
        contentPhotos?.find((x) => x.type === type && x.device === "TV")?.src ||
        contentPhotos?.find((x) => x.type === type)?.src ||
        contentPhotos?.find((x) => x.type === "poster")?.src
    );

/**
 * Get image for vertical cover
 * @param {Array} contentPhotos - Array of photo objects
 * @returns {object} { src }
 */
export const getVerticalCoverImage = (contentPhotos = []) =>
    getOptimizedImageForType("verticalCover", contentPhotos, "web");

/**
 * Get image for horizontal/slider cover
 * @param {Array} contentPhotos - Array of photo objects
 * @returns {object} { src }
 */
export const getSliderCoverImage = (contentPhotos = []) =>
    getOptimizedImageForType("sliderCover", contentPhotos, "web");

/**
 * Get image for poster
 * @param {Array} contentPhotos - Array of photo objects
 * @returns {object} { src }
 */
export const getPosterImage = (contentPhotos = []) =>
    getOptimizedImageForType("poster", contentPhotos, "web");

/**
 * Get image for video logo
 * @param {Array} contentPhotos - Array of photo objects
 * @returns {object} { src }
 */
export const getVideoLogoImage = (contentPhotos = []) =>
    getOptimizedImageForType("videoLogo", contentPhotos, "web", { format: 'png' });

/**
 * Tags guide for content categorization
 */
export const TURKISH_NATIVE_BADGE_SRC =
    "/tags/turkish-alt.svg?v=44ebfa395ecf";

export const tagsGuide = {
    "ترکی دیلینده": "TURKISH_NATIVE",
    "گزارش ترکی": "TURKISH_REPORT",
    "پخش زنده": "LIVE_STREAM",
    بزودی: "COMING_SOON",
    "آنلاین باخیش": "ONLINE_CINEMA",
    آرشیو: "ARCHIVE",
};

export const getDisplayTagLabel = (label, tags = []) => {
    const tagLabels = Array.isArray(tags) ? tags : [];
    const turkishReportLabel = tagLabels.find(
        (tagLabel) => tagsGuide[tagLabel] === "TURKISH_REPORT"
    );

    return turkishReportLabel || label;
};

const cmsUtils = {
    getCMSImageURL,
    getOptimizedImageData,
    getOptimizedImageForType,
    getImageForType,
    getVerticalCoverImage,
    getSliderCoverImage,
    getPosterImage,
    getVideoLogoImage,
    TURKISH_NATIVE_BADGE_SRC,
    tagsGuide,
    getDisplayTagLabel,
};

export default cmsUtils;
